// src/services/orderDeskDirectClient.js
// Direct OrderDesk API client for admin operations
// DOES NOT AFFECT CART SUBMISSIONS - those continue to use existing orderDeskAPI.js

import apiMonitor from './apiMonitor.js';
import orderDeskErrorHandler from './orderDeskErrorHandler.js';

class RateLimiter {
    constructor(maxRequests, windowMs) {
        this.maxRequests = maxRequests;
        this.windowMs = windowMs;
        this.requests = [];
    }

    async waitForSlot() {
        const now = Date.now();
        
        // Remove old requests outside the window
        this.requests = this.requests.filter(time => now - time < this.windowMs);
        
        if (this.requests.length >= this.maxRequests) {
            const oldestRequest = Math.min(...this.requests);
            const waitTime = this.windowMs - (now - oldestRequest);
            
            if (waitTime > 0) {
                console.log(`[OrderDeskDirectClient] Rate limit reached, waiting ${waitTime}ms`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                return this.waitForSlot();
            }
        }
        
        this.requests.push(now);
    }
}

class OrderDeskDirectClient {
    constructor() {
        this.baseURL = 'https://app.orderdesk.me/api/v2';
        this.storeId = process.env.REACT_APP_ORDER_DESK_STORE_ID;
        this.apiKey = process.env.REACT_APP_ORDER_DESK_API_KEY;
        this.rateLimiter = new RateLimiter(100, 30000); // 100 requests per 30 seconds

        if (!this.storeId || !this.apiKey) {
            console.warn('[OrderDeskDirectClient] Missing ORDER_DESK credentials. Check .env file has REACT_APP_ORDER_DESK_STORE_ID and REACT_APP_ORDER_DESK_API_KEY');
        } else {
            console.log('[OrderDeskDirectClient] Initialized with Store ID:', this.storeId);
        }
    }

    /**
     * Make a direct API call to OrderDesk with retry logic and rate limiting
     */
    async makeRequest(endpoint, options = {}) {
        const { method = 'GET', body, params, retries = 3, timeout = 30000, operation } = options;
        
        // Wait for rate limiter
        await this.rateLimiter.waitForSlot();
        
        // Build URL with query parameters
        const url = new URL(`${this.baseURL}${endpoint}`);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    url.searchParams.set(key, String(value));
                }
            });
        }
        
        // Build headers
        const headers = {
            'ORDERDESK-STORE-ID': this.storeId,
            'ORDERDESK-API-KEY': this.apiKey,
        };
        
        if (method !== 'GET' && method !== 'DELETE') {
            headers['Content-Type'] = 'application/json';
        }
        
        // Build request options
        const requestOptions = {
            method,
            headers,
            signal: AbortSignal.timeout(timeout),
        };
        
        if (body && (method === 'POST' || method === 'PUT')) {
            requestOptions.body = JSON.stringify(body);
        }
        
        if (process.env.NODE_ENV === 'development') {
            console.log(`[OrderDeskDirectClient] ${method} ${endpoint}`);
        }

        // Start API monitor tracking
        const requestId = apiMonitor.startRequest(operation || `${method} ${endpoint}`, {
            method,
            endpoint,
            requestData: body
        });
        
        let lastError;
        
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await fetch(url.toString(), requestOptions);
                
                // Handle rate limiting
                if (response.status === 429) {
                    const retryAfter = parseInt(response.headers.get('x-retry-after') || '2', 10);
                    console.log(`[OrderDeskDirectClient] Rate limited, retrying after ${retryAfter}s`);
                    await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
                    continue;
                }
                
                // Parse response
                let data = null;
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    data = await response.json();
                }
                
                // Handle success
                if (response.ok) {
                    if (process.env.NODE_ENV === 'development') {
                        console.log(`[OrderDeskDirectClient] Success: ${method} ${endpoint}`);
                    }
                    const resultPayload = {
                        success: true,
                        data,
                        status: response.status,
                        headers: response.headers,
                        meta: {
                            tokensRemaining: parseInt(response.headers.get('x-tokens-remaining') || '0', 10),
                            retryAfter: parseInt(response.headers.get('x-retry-after') || '0', 10),
                            requestId: response.headers.get('x-request-id'),
                        }
                    };
                    // Complete monitoring
                    await apiMonitor.completeRequest(requestId, resultPayload, null);
                    return resultPayload;
                }
                
                // Handle API errors
                const error = new Error(data?.error || data?.message || `HTTP ${response.status}: ${response.statusText}`);
                error.status = response.status;
                error.data = data;
                error.response = response;
                
                // Don't retry client errors (4xx) except 429
                if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                    throw error;
                }
                
                lastError = error;
                // Log error
                await orderDeskErrorHandler.logError(error, {
                    operation: operation || `${method} ${endpoint}`,
                    endpoint,
                    method,
                    requestData: body
                });
                
            } catch (error) {
                lastError = error;
                await orderDeskErrorHandler.logError(error, {
                    operation: operation || `${method} ${endpoint}`,
                    endpoint,
                    method,
                    requestData: body
                });
                
                // Don't retry on abort/timeout
                if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                    break;
                }
                
                // Exponential backoff for retries
                if (attempt < retries) {
                    const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
                    console.log(`[OrderDeskDirectClient] Attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error.message);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }
        
        console.error(`[OrderDeskDirectClient] All attempts failed for ${method} ${endpoint}:`, lastError);
        // Complete monitoring with error
        await apiMonitor.completeRequest(requestId, null, lastError);
        throw lastError;
    }

    /**
     * Validate required fields for OrderDesk API
     */
    validateOrderData(orderData, isUpdate = false) {
        const errors = [];
        
        if (!isUpdate) {
            // Required for new orders
            if (!orderData.source_id) errors.push('source_id is required');
            if (!orderData.order_items || !Array.isArray(orderData.order_items) || orderData.order_items.length === 0) {
                errors.push('order_items array is required and must not be empty');
            }
        }
        
        // Validate order items
        if (orderData.order_items) {
            orderData.order_items.forEach((item, index) => {
                if (!item.name) errors.push(`order_items[${index}].name is required`);
                if (!item.code) errors.push(`order_items[${index}].code is required`);
                if (typeof item.quantity !== 'number' || item.quantity <= 0) {
                    errors.push(`order_items[${index}].quantity must be a positive number`);
                }
                if (typeof item.price !== 'string' && typeof item.price !== 'number') {
                    errors.push(`order_items[${index}].price must be a string or number`);
                }
            });
        }
        
        // Validate totals
        if (orderData.order_total !== undefined && (typeof orderData.order_total !== 'string' && typeof orderData.order_total !== 'number')) {
            errors.push('order_total must be a string or number');
        }
        
        if (errors.length > 0) {
            throw new Error(`Order validation failed: ${errors.join(', ')}`);
        }
        
        return true;
    }

    /**
     * Normalize order data for OrderDesk API
     */
    normalizeOrderData(orderData) {
        const normalized = { ...orderData };
        
        // Ensure numeric fields are properly formatted
        if (normalized.order_total !== undefined) {
            normalized.order_total = typeof normalized.order_total === 'number' 
                ? normalized.order_total.toFixed(2) 
                : String(normalized.order_total);
        }
        
        if (normalized.shipping_total !== undefined) {
            normalized.shipping_total = typeof normalized.shipping_total === 'number' 
                ? normalized.shipping_total.toFixed(2) 
                : String(normalized.shipping_total);
        }
        
        if (normalized.tax_total !== undefined) {
            normalized.tax_total = typeof normalized.tax_total === 'number' 
                ? normalized.tax_total.toFixed(2) 
                : String(normalized.tax_total);
        }
        
        if (normalized.handling_total !== undefined) {
            normalized.handling_total = typeof normalized.handling_total === 'number' 
                ? normalized.handling_total.toFixed(2) 
                : String(normalized.handling_total);
        }
        
        // Normalize order items
        if (normalized.order_items) {
            normalized.order_items = normalized.order_items.map(item => {
                const next = {
                    ...item,
                    quantity: Number(item.quantity) || 1,
                    price: typeof item.price === 'number' ? item.price.toFixed(2) : String(item.price || '0.00'),
                };
                if (item.weight) next.weight = Number(item.weight);
                else delete next.weight;
                return next;
            });
        }
        
        // Ensure customer object exists
        if (!normalized.customer) {
            normalized.customer = {};
        }
        
        // Ensure shipping object exists
        if (!normalized.shipping) {
            normalized.shipping = {};
        }
        
        return normalized;
    }

    /**
     * Health check - test API connectivity
     */
    async healthCheck() {
        try {
            const result = await this.makeRequest('/test');
            return {
                success: true,
                message: 'OrderDesk API connection successful',
                data: result.data,
                meta: result.meta
            };
        } catch (error) {
            return {
                success: false,
                message: 'OrderDesk API connection failed',
                error: error.message,
                status: error.status
            };
        }
    }
}

export default OrderDeskDirectClient;
